// La sidebar de la interfaz DE SIEMPRE: los grupos Imagen / Video, cada uno con su
// "Generar…" (a `/modos`) y los dos modos que el usuario usó últimamente. Vivía en
// `EstiloMinimal` hasta ADR-2026-10-05-la-sidebar-por-secciones, que la reemplazó por las
// tres secciones con panel; vuelve en su archivo porque las dos conviven: ésta para todo
// grupo que no es C, `SeccionesDeLaSidebar` para el grupo C
// (ADR-2026-10-07-las-dos-sidebars-conviven). Copiada de `principal` (17d80457) sin
// cambios de comportamiento: lo único que cambió es que el candado, el click con candado y
// las clases de la fila los pone el shell por props, igual que a la otra sidebar.

import { Fragment, type MouseEvent as ReactMouseEvent } from 'react';
import { EnlaceApp } from '../../components/EnlaceApp';
import { useModosRecientes } from '../../hooks/useModosRecientes';
import { recientesDeFamilia } from '../../lib/modosRecientes';
import { nombreCortoModo } from '../v2/modesCatalog';
import type { HerramientaAtlas } from './contenidoAtlas';

/**
 * Cuántos modos muestra cada grupo — ADR-2026-09-19-la-sidebar-muestra-los-dos-modos-mas-usados.
 *
 * Dos, y son los dos ÚLTIMOS que ese usuario abrió de esa familia. Antes eran tres
 * elegidos a mano (`destacados`, ADR-2026-08-31-los-destacados-de-la-sidebar-van-elegidos-a-mano):
 * una decisión de producto sobre qué queremos que se use, que le ganaba al modo con el
 * que la persona de verdad trabaja todos los días. La puerta al catálogo completo
 * ahora ABRE el grupo ("Generar imagen" / "Generar video") en vez de cerrarlo, así que
 * descubrir el resto no depende de que los destacados adivinen bien.
 */
const MODOS_POR_GRUPO = 2;

/**
 * Los dos grupos de la sidebar — ADR-2026-08-31-la-sidebar-agrupa-por-video-e-imagen.
 *
 * Ya no llevan `visibles` ni `destacados`: los dos grupos muestran `MODOS_POR_GRUPO` y
 * cuáles lo dice el uso (supersede ADR-2026-08-31-los-destacados-de-la-sidebar-van-elegidos-a-mano).
 */
const GRUPOS_HERRAMIENTAS: {
  familia: 'video' | 'imagen';
  rotulo: string;
  /** El acceso al catálogo que encabeza el grupo. */
  ctaGenerar: string;
}[] = [
  // Imagen va PRIMERO — ADR-2026-08-31-imagen-va-antes-que-video-en-la-sidebar.
  // Es lo que la mayoría viene a hacer y el grupo con más modos; video, que es lo
  // nuevo, queda abajo pero igual arriba del pliegue.
  { familia: 'imagen', rotulo: 'Imagen', ctaGenerar: 'Generar imagen' },
  { familia: 'video', rotulo: 'Video', ctaGenerar: 'Generar video' },
];

/**
 * Las que se muestran de un grupo: los `MODOS_POR_GRUPO` últimos modos que el usuario
 * abrió de esa familia, dibujados en el orden del catálogo.
 *
 * La elección es por USO y el orden es por CATÁLOGO, que son dos preguntas distintas:
 * si la lista se ordenara por uso, tocar el ítem de abajo lo haría saltar al primer
 * lugar justo debajo del cursor que lo acaba de tocar.
 *
 * **Sin usos, el grupo no muestra ningún modo** — queda su rótulo y su "Generar…", que
 * es la puerta al catálogo. Una cuenta nueva no tiene de dónde sacar dos favoritos, y
 * inventarlos es volver a los destacados.
 *
 * Acá vivía además la regla de "la activa siempre está", que reemplazaba la última fila
 * por la herramienta en la que estaba parado el usuario cuando no entraba en el corte.
 * Ya no hace falta: entrar a un modo lo vuelve el más reciente de su familia, así que
 * la activa está SIEMPRE por construcción. Sí cambió una cosa: el grupo ya no mide
 * siempre lo mismo (arranca vacío y crece hasta dos), pero crece de a una fila y sólo
 * las dos primeras veces que se usa la app.
 */
function grupoVisible(
  herramientas: HerramientaAtlas[],
  recientes: readonly string[],
): HerramientaAtlas[] {
  return recientesDeFamilia(herramientas, recientes, MODOS_POR_GRUPO);
}

/**
 * Destino de un ítem de herramientas — ADR-2026-07-31-shell-nuevo-urls-viejas.
 *
 * Acá vivía `RUTA_EN_SHELL` + `RUTA_MODO_VIEJA`, la tabla que traducía cada ruta de
 * `contenidoAtlas` (`/interfaz-v2/modo/:mode`, `/interfaz-v2/plantillas`,
 * `/editor-video`) a un clon hospedado (`/modo/:mode`, `/plantillas`,
 * `/estudio-video`). Esa traducción existía porque las rutas originales montaban
 * `DashboardLayout` con su propia sidebar y había que esquivarlas.
 *
 * Ya no hace falta: son esas MISMAS rutas las que ahora se montan dentro de este
 * shell, así que el destino correcto es el que trae el catálogo y la función queda
 * en la identidad. Se deja como función —y no se reemplazan los llamados por
 * `h.ruta`— porque es el único punto donde habría que volver a intervenir si
 * alguna herramienta necesitara un destino propio en este shell.
 */
function rutaSidebar(h: { id: string; ruta: string }): string {
  return h.ruta;
}

/**
 * Nombre de una herramienta en la sidebar — ADR-2026-07-31-solo-nombre-modo-sidebar-toma:
 * el del catálogo (`COPY_MODOS`), para que un modo se llame igual acá y en la home. Sin la
 * opción `uiNueva`: ésta es la sidebar de siempre, así que Plantillas dice "Plantillas"
 * (ADR-2026-10-07-plantillas-en-la-vieja-recrear-en-la-nueva). El respaldo a `nombre`
 * queda para una herramienta nueva que se sume a `HERRAMIENTAS_EXTRA` sin entrada ahí.
 */
function etiquetaSidebar(h: { id: string; nombre: string }): string {
  return nombreCortoModo(h.id, h.nombre);
}

/**
 * Las herramientas que esta sidebar lista: todas menos Crear (`redes`). En la interfaz de
 * siempre la puerta a Crear es Prompt libre (que en escritorio abre `/redes`,
 * ADR-2026-10-07-prompt-libre-y-creador-abren-crear); como el shell anota como reciente
 * cada ruta que ve, abrir Prompt libre anotaba `prompt-libre` Y `redes`, y el grupo Imagen
 * mostraba "Prompt libre" y "Crear": dos puertas al mismo lugar.
 */
export function sinCrear<T extends { id: string }>(herramientas: readonly T[]): T[] {
  return herramientas.filter((h) => h.id !== 'redes');
}

export function GruposDeLaSidebar({
  herramientas,
  estaBloqueada,
  pathname,
  colapsada,
  irA,
  alBloqueada,
  claseFila,
  propsTooltip,
  abrirCardAyuda,
  cerrarCardAyuda,
}: {
  /** Los modos que este usuario ve (rubro, flags y dev ya filtrados), ya ordenados. */
  herramientas: readonly HerramientaAtlas[];
  /** El candado por plan de un modo, por su `path` (el mismo criterio que la otra sidebar). */
  estaBloqueada: (path: string) => boolean;
  pathname: string;
  colapsada: boolean;
  irA: (ruta: string) => void;
  /** Una fila con candado no navega: abre los planes. */
  alBloqueada: () => void;
  /** Las clases de una fila (activa / con candado), las mismas que el resto de la nav. */
  claseFila: (activa: boolean, bloqueada: boolean) => string;
  propsTooltip: (texto: string) => Record<string, unknown>;
  /** La card flotante de ayuda de la sidebar (`useCardAyudaSidebar`). */
  abrirCardAyuda: (el: HTMLElement, titulo: string, texto: string, nota?: string) => void;
  cerrarCardAyuda: () => void;
}) {
  const modosRecientes = useModosRecientes();
  const lista = sinCrear(herramientas);
  return (
    <>
      {GRUPOS_HERRAMIENTAS.map((grupo, iGrupo) => {
        const items = grupoVisible(
          lista.filter((h) => h.familia === grupo.familia),
          modosRecientes,
        );
        // Un grupo SIN modos igual se dibuja: le pasa a toda cuenta nueva, y lo
        // que queda —el rótulo y "Generar…"— es justamente la puerta al catálogo
        // que esa persona necesita. Antes acá se devolvía `null` porque un grupo
        // sin modos no tenía nada que ofrecer; ahora sí.
        return (
          <Fragment key={grupo.familia}>
            {/* Rótulo del grupo, no un ítem: sin ícono, sin click y más chico que
                la navegación. Colapsada no hay ancho para la palabra. */}
            {!colapsada && (
              <p
                /* Más apretado que el rótulo único que había antes, y con
                   `leading-none`: ahora son DOS encabezados y cada píxel que se
                   llevan sale del cupo de la lista, que es la que tiene que entrar
                   sin scrollear. Con el interlineado por defecto (15,75px para un
                   texto de 10,5) cada rótulo se llevaba 5px de aire invisible. */
                className={`mb-0.5 px-2.5 text-[10.5px] font-semibold uppercase leading-none tracking-[0.09em] text-black/35 ${
                  iGrupo > 0 ? 'mt-1' : ''
                }`}
              >
                {grupo.rotulo}
              </p>
            )}
            {/* Colapsada no hay rótulos, así que el aire entre grupos es lo ÚNICO
                que los separa: sin esto los dos bloques de íconos se leen como una
                lista sola y el agrupado desaparece. */}
            {colapsada && iGrupo > 0 && <div className="h-2" />}
            {/* "Generar imagen" / "Generar video" ENCABEZA el grupo y lleva a
                `/modos` con su familia en el query —
                ADR-2026-09-19-la-sidebar-muestra-los-dos-modos-mas-usados,
                conserva ADR-2026-08-31-ver-mas-de-la-sidebar-enfoca-su-familia (la
                pantalla abre con los modos de este grupo y deja la otra familia
                plegada al pie).

                Era "Ver más", cerraba el grupo y venía en negro al 45%: el
                tratamiento tenue decía "esto es secundario, lo importante son las
                filas de arriba". Con dos modos que dependen de lo que usaste —y
                ninguno en una cuenta nueva—, la puerta al catálogo pasó a ser la
                acción principal del grupo, así que sube al tope y toma el peso
                normal de la lista. El `add` es lo que la vuelve una acción y no un
                destino más; los tres puntos de antes decían "hay más de esto acá",
                que es otra cosa. */}
            <button
              onClick={() => {
                cerrarCardAyuda();
                irA(`/modos?familia=${grupo.familia}`);
              }}
              {...propsTooltip(grupo.ctaGenerar)}
              aria-label={grupo.ctaGenerar}
              // Las mismas clases que una fila sin activar y sin candado.
              className={claseFila(false, false)}
            >
              <span className="material-symbols-rounded shrink-0 text-[19px]">
                add
              </span>
              {!colapsada && <span className="truncate">{grupo.ctaGenerar}</span>}
            </button>
            {items.map((h) => {
        const destino = rutaSidebar(h);
        // Las herramientas ahora son secciones de este shell (ADR-2026-07-31-8-herramientas-sidebar-hospedan-shell), así que
        // se marcan y se cruzan con el mismo fade que Inicio/Creaciones/Editar
        // marca. `startsWith` y no `===` porque el modo abre sub-rutas.
        const activa = pathname === destino || pathname.startsWith(`${destino}/`);
        // Candado en lugar del ícono cuando el plan no incluye la herramienta
        // (ADR-2026-08-03-sidebar-candado-modos-sin-plan). El criterio lo pone el
        // shell (`estaBloqueada`: `getModePlanLock` sobre el mismo plan, el mismo que
        // usa la sidebar por secciones y la tira de la home), no una regla propia:
        // la lista y las cards tienen que decir lo mismo de lo mismo.
        const bloqueada = estaBloqueada(h.id);
        return (
          <EnlaceApp
            key={h.id}
            // Con candado NO hay href: el ítem no lleva al modo sino al catálogo de
            // planes, así que tampoco tiene que poder abrirse en otra pestaña.
            href={bloqueada ? undefined : destino}
            // Con candado no se navega: se abre el catálogo de planes
            // (ADR-2026-08-03-candado-de-sidebar-abre-el-catalogo). Antes el ítem
            // llevaba igual al modo y era la propia pantalla la que lo rebotaba
            // —abría `SubscriptionLimitModal` y volvía a la home—, así que el
            // usuario iba y venía para terminar en un cartel distinto del que
            // muestran los otros accesos bloqueados de la app.
            //
            // El motivo es `'bloqueado'`, que ya existía y trae el copy de
            // "Desbloqueá todo el potencial de Estudio Atlas": el momento es ese
            // —le mostraron que hay más Atlas del que puede usar— y no tiene nada
            // que ver con el saldo, que es lo que separa este motivo de
            // `sin`/`pocos`.
            onNavegar={() => {
              cerrarCardAyuda();
              if (bloqueada) alBloqueada();
              else irA(destino);
            }}
            // Herramientas CON descripción llevan el card de ayuda (la
            // aclaración de plan pasa a ser la nota del card); sin
            // descripción (hoy no hay ninguna) cae al tooltip de sidebar
            // colapsada, con la aclaración pegada a la etiqueta.
            {...(h.descripcion
              ? {
                  onMouseEnter: (e: ReactMouseEvent<HTMLElement>) =>
                    abrirCardAyuda(
                      e.currentTarget,
                      etiquetaSidebar(h),
                      h.descripcion!,
                      bloqueada
                        ? 'Incluido en los planes pagos'
                        : undefined,
                    ),
                  onMouseLeave: cerrarCardAyuda,
                }
              : propsTooltip(
                  `${etiquetaSidebar(h)}${bloqueada ? ' — incluido en los planes pagos' : ''}`,
                ))}
            className={claseFila(activa, bloqueada)}
          >
            <span className="material-symbols-rounded shrink-0 text-[19px]">
              {bloqueada ? 'lock' : h.icono}
            </span>
            {!colapsada && <span className="truncate">{etiquetaSidebar(h)}</span>}
          </EnlaceApp>
        );
            })}

          </Fragment>
        );
      })}
    </>
  );
}
